/**
 * GA4 (Firebase Analytics) conversion events + internal-traffic exclusion.
 *
 * Only a few events go to GA: sign_up, begin_checkout, purchase. Everything
 * else stays in Phi analytics / Firestore funnel counters (phiTrack).
 */
import { getAdditionalUserInfo } from 'firebase/auth';
import { logEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { analyticsReady } from '../firebase';
import { rememberSignedIn } from './authMemory';
import { recordFunnelEvent } from './siteStats';

const INTERNAL_KEY = 'cfa_internal_traffic';
const SIGNUP_KEY = (uid) => `cfa_ga_signup_${uid}`;
const SIGNUP_WINDOW_MS = 10 * 60 * 1000;

const GA_EVENT = {
  checkout_started: 'begin_checkout',
  checkout_paid: 'purchase',
};

/** Tolerates mocks / environments where the Firebase module has no analyticsReady. */
const ready = () => Promise.resolve(analyticsReady).catch(() => null);

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Owner/admin visits: /admin pages, or a browser that has been seen signed in as admin. */
export function isInternalTraffic() {
  if (typeof window === 'undefined') return false;
  if (/^\/admin(\/|$)/.test(window.location.pathname || '')) return true;
  return storage()?.getItem(INTERNAL_KEY) === '1';
}

/** Remember this browser as internal and stop GA collection now. */
export function markInternalTraffic() {
  try {
    storage()?.setItem(INTERNAL_KEY, '1');
  } catch {
    /* private mode */
  }
  ready()
    .then((analytics) => {
      if (analytics) setAnalyticsCollectionEnabled(analytics, false);
    })
    .catch(() => {});
}

function send(name, params) {
  if (isInternalTraffic()) return;
  ready()
    .then((analytics) => {
      if (analytics && !isInternalTraffic()) logEvent(analytics, name, params);
    })
    .catch(() => {});
}

function numericPrice(props) {
  const v = Number(props.priceUsd ?? props.amountUsd ?? props.value);
  return Number.isFinite(v) && v > 0 ? v : null;
}

/** Map a phiTrack funnel event to its GA4 recommended event, if it has one. */
export function forwardToGa(eventName, props = {}) {
  const name = GA_EVENT[eventName];
  if (!name) return;
  const params = { currency: 'USD' };
  const value = numericPrice(props);
  if (value != null) params.value = value;
  if (props.sku) params.items = [{ item_id: String(props.sku), item_name: String(props.sku) }];
  if (props.transactionId) params.transaction_id = String(props.transactionId).slice(0, 100);
  for (const key of ['placement', 'source', 'level', 'pricingTier', 'country']) {
    if (props[key]) params[key] = String(props[key]).slice(0, 100);
  }
  if (typeof props.guest === 'boolean') params.guest = props.guest;
  send(name, params);
}

function signUpMethod(user) {
  const provider = user.providerData?.[0]?.providerId;
  if (provider === 'google.com') return 'google';
  if (provider === 'password') return 'email';
  return provider ? provider.replace(/\.com$/, '') : 'purchase';
}

function isFreshAccount(user) {
  const created = Date.parse(user?.metadata?.creationTime || '');
  return Number.isFinite(created) && Date.now() - created <= SIGNUP_WINDOW_MS;
}

/**
 * Once per uid: GA4 sign_up + siteStats account_created (+ method breakdown).
 * Safe to call from credential paths and onAuthStateChanged; deduped in localStorage.
 */
function markAccountCreated(user) {
  if (!user?.uid) return false;
  const store = storage();
  if (store?.getItem(SIGNUP_KEY(user.uid)) === '1') return false;
  try {
    store?.setItem(SIGNUP_KEY(user.uid), '1');
  } catch {
    /* private mode */
  }
  const method = signUpMethod(user);
  send('sign_up', { method });
  // Site counter for every method (the email form's free_mock_signup misses Google/purchase).
  // Do not await — SPA navigation must not cancel the keepalive POST.
  Promise.resolve(recordFunnelEvent('account_created')).catch(() => {});
  Promise.resolve(recordFunnelEvent(`account_created__${method}`)).catch(() => {});
  rememberSignedIn(user);
  return true;
}

/**
 * Preferred path: fire from the Auth credential (email create / Google popup / redirect)
 * using getAdditionalUserInfo(...).isNewUser so we do not depend only on creationTime clocks.
 */
export function trackAccountCreatedFromCredential(cred) {
  if (!cred?.user?.uid) return false;
  let isNew = false;
  try {
    const info = getAdditionalUserInfo(cred);
    if (info && typeof info.isNewUser === 'boolean') {
      isNew = info.isNewUser;
    } else {
      // Mocks / older SDK paths may omit additionalUserInfo.
      isNew = isFreshAccount(cred.user);
    }
  } catch {
    isNew = isFreshAccount(cred.user);
  }
  if (!isNew) {
    rememberSignedIn(cred.user);
    return false;
  }
  return markAccountCreated(cred.user);
}

/** Fallback for onAuthStateChanged when no credential is available (e.g. guest purchase). */
export function trackSignUpIfNew(user) {
  if (!user?.uid || !isFreshAccount(user)) return;
  markAccountCreated(user);
}

/** Custom GA4 event (respects internal-traffic exclusion). */
export function trackGaEvent(name, params = {}) {
  send(name, params);
}
